import { useState, useEffect, useCallback, useRef } from 'react';
import * as api from '../../../api/api';
import itRosterData from '../../../data/it_roster.json';
import {
  performVerifiedSilentRefresh,
  shouldPerformSilentRefresh,
  getProjectYear,
  persistRoleUpdates,
  cleanupCorruptedAdjustmentInfo,
  convertToMonthly,
  determineRateType,
  validateAnnualWorkingDays,
  calculateCOLAAdjustedRate
} from '../../../utils/rateUtils';
import { isCacheClearNeeded, markCachesCleared } from '../../../utils/cacheInvalidation';

export const useStaffingData = (outcomeId, outcomeData, initialFormData) => {
  // Basic data state
  const [itRoster, setItRoster] = useState([]);
  const [rateData, setRateData] = useState([]);
  const [locationOptions, setLocationOptions] = useState({ 'CA': [] });
  const [roleOptions, setRoleOptions] = useState({ 'CA': { 'US': [] } });
  
  // Loading and processing state
  const [loading, setLoading] = useState(false);
  const [basicDataReady, setBasicDataReady] = useState(false);
  const [enhancementProgress, setEnhancementProgress] = useState({
    rates: 'idle',
    locations: 'idle',
    silentRefresh: 'idle'
  });
  
  // Silent refresh state
  const [silentRefreshInProgress, setSilentRefreshInProgress] = useState(false);
  const silentRefreshCompleted = useRef(false);
  const [silentRefreshResult, setSilentRefreshResult] = useState(null);
  const [roleUpdateIndicators, setRoleUpdateIndicators] = useState(new Map());
  const [roleCorruptionIndicators, setRoleCorruptionIndicators] = useState(new Map());
  const [refreshingRates, setRefreshingRates] = useState(false);
  const [rateUpdateCount, setRateUpdateCount] = useState(0);
  const [metadataUpdateCount, setMetadataUpdateCount] = useState(0);
  
  // Error handling
  const [error, setError] = useState(null);

  // Load basic data (roster, rates, locations)
  const loadBasicData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      if (isCacheClearNeeded()) {
        api.clearStaffingCache(outcomeId ? [outcomeId] : null);
        markCachesCleared();
      }

      // Load IT Roster
      try {
        console.log('🔄 StaffingData: Loading IT roster...');
        const rosterResponse = await api.getITRoster();
        const rosterData = rosterResponse.data || rosterResponse || [];
        const validMembers = Array.isArray(rosterData) ? rosterData.filter(member => 
          member && member.id && (member.name || member.first_name || member.last_name)
        ) : [];
        setItRoster(validMembers);
        console.log('✅ StaffingData: Loaded', validMembers.length, 'roster members');
      } catch (error) {
        console.warn('⚠️ StaffingData: API not available for IT roster, using local data:', error);
        const localData = itRosterData || [];
        const validLocalMembers = Array.isArray(localData) ? localData.filter(member => 
          member && member.id && (member.name || member.first_name || member.last_name)
        ) : [];
        setItRoster(validLocalMembers);
        console.log('✅ StaffingData: Loaded', validLocalMembers.length, 'local roster members');
      }

      setEnhancementProgress(prev => ({ ...prev, rates: 'loading' }));
      
      // Load rates data
      console.log('🔄 StaffingData: Loading rates data...');
      const ratesResponse = await api.getRates();
      const rates = ratesResponse.data?.rates || ratesResponse.rates || [];
      const processedRates = rates.map(rate => ({
        ...rate,
        dailyRate: rate.dailyRate,
        monthlyRate: convertToMonthly(rate.dailyRate, rate.annualWorkingDays),
        rateType: determineRateType(rate.annualWorkingDays)
      }));
      setRateData(processedRates);
      console.log('✅ StaffingData: Loaded', processedRates.length, 'rates');

      setEnhancementProgress(prev => ({ ...prev, rates: 'complete', locations: 'loading' }));

      // Build location/role options
      const companies = [...new Set(processedRates.map(rate => rate.company))].sort();
      if (companies.length > 0) {
        const locationsByCompany = {};
        const rolesByCompanyLocation = {};

        companies.forEach(company => {
          const companyRates = processedRates.filter(rate => rate.company === company);
          const locationCodes = [...new Set(companyRates.map(rate => rate.location))].sort();

          locationsByCompany[company] = locationCodes.map(code => ({
            code: code,
            display: code
          }));

          rolesByCompanyLocation[company] = {};
          locationCodes.forEach(locationCode => {
            const locationRates = companyRates.filter(rate => rate.location === locationCode);
            const roles = [...new Set(locationRates.map(rate => rate.role))].sort();
            rolesByCompanyLocation[company][locationCode] = roles;
          });
        });

        setLocationOptions(locationsByCompany);
        setRoleOptions(rolesByCompanyLocation);
        console.log('✅ StaffingData: Built location options for', companies.length, 'companies');
      }

      setEnhancementProgress(prev => ({ ...prev, locations: 'complete' }));
      setBasicDataReady(true);
      
    } catch (error) {
      console.error('❌ StaffingData: Error loading basic data:', error);
      setError(error.message);
      setEnhancementProgress(prev => ({ 
        ...prev, 
        rates: 'failed', 
        locations: 'failed' 
      }));
    } finally {
      setLoading(false);
    }
  }, []);

  // Perform silent refresh
  const performSilentRefresh = useCallback(async (allRoles, projectYear) => {
    if (silentRefreshInProgress || silentRefreshCompleted.current) {
      return { updated: false, reason: 'already_completed' };
    }

    try {
      setSilentRefreshInProgress(true);
      setEnhancementProgress(prev => ({ ...prev, silentRefresh: 'loading' }));
      
      console.log('🔄 StaffingData: Starting silent refresh for', allRoles.length, 'roles');

      // Clean up corrupted data first
      const cleanupResult = cleanupCorruptedAdjustmentInfo(allRoles);
      if (cleanupResult.cleanedCount > 0) {
        console.log(`🧹 StaffingData: Cleaned up ${cleanupResult.cleanedCount} corrupted records`);
      }

      // Check if refresh is needed
      if (!shouldPerformSilentRefresh(allRoles, itRoster, projectYear)) {
        console.log('ℹ️ StaffingData: Silent refresh not needed');
        silentRefreshCompleted.current = true;
        setEnhancementProgress(prev => ({ ...prev, silentRefresh: 'complete' }));
        return { updated: false, reason: 'not_needed' };
      }

      // Perform the refresh
      const refreshResult = await performVerifiedSilentRefresh(allRoles, itRoster, outcomeId, projectYear);
      console.log('✅ StaffingData: Silent refresh completed:', refreshResult);

      // Handle corruption indicators
      const corruptionIndicators = new Map();
      allRoles.forEach(role => {
        if (role.roster_member_id && role.rateAdjustmentInfo) {
          // Check for corruption patterns
          const baseRate = role.rateAdjustmentInfo.baseRate;
          const annualWorkingDays = role.rateAdjustmentInfo.annualWorkingDays;
          
          if (baseRate > 50000 && annualWorkingDays > 200) {
            corruptionIndicators.set(role.id, {
              type: 'rate_corruption',
              details: `Corrupted rate data: baseRate $${baseRate.toLocaleString()} with ${annualWorkingDays} working days`,
              baseRate,
              annualWorkingDays,
              roleName: role.name,
              outcomeId: outcomeId,
              severity: 'error'
            });
          }
        }
      });

      if (corruptionIndicators.size > 0) {
        setRoleCorruptionIndicators(corruptionIndicators);
      }

      // Create update indicators
      if (refreshResult.updated && refreshResult.roleUpdates) {
        const updateIndicators = new Map();
        refreshResult.roleUpdates.forEach(update => {
          const roleId = update.roleId || update.role_id;
          if (roleId) {
            updateIndicators.set(roleId, {
              type: update.rateChanged && update.metadataChanged ? 'both' : 
                    update.rateChanged ? 'rate' : 'metadata',
              oldValue: update.oldRate,
              newValue: update.newRate,
              details: update.rateChanged && update.metadataChanged ? 
                      'Rate and member information updated' : 
                      update.rateChanged ? 'Rate updated' : 'Member information updated'
            });
          }
        });
        
        setRoleUpdateIndicators(updateIndicators);
        
        // Clear indicators after 10 seconds
        setTimeout(() => {
          setRoleUpdateIndicators(new Map());
        }, 10000);
      }

      // Expose updated roles so callers can merge them into formData,
      // and notify Roster / FVA so they re-fetch the newly-saved rates.
      if (refreshResult.updated) {
        setSilentRefreshResult(refreshResult);
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('outcomeRatesUpdated', {
            detail: {
              outcomeIds: [outcomeId],
              source: 'staffing_silent_refresh',
              updateInfo: {
                rateUpdatesCount: refreshResult.rateUpdatesCount,
                metadataUpdatesCount: refreshResult.metadataUpdatesCount
              }
            }
          }));
        }
      }

      // Persist updates if needed
      if (refreshResult.rolesToUpdate && refreshResult.rolesToUpdate.length > 0) {
        try {
          await persistRoleUpdates(refreshResult.rolesToUpdate, outcomeId);
          console.log('✅ StaffingData: Persisted silent refresh updates');
        } catch (persistError) {
          console.error('❌ StaffingData: Failed to persist updates:', persistError);
        }
      }

      silentRefreshCompleted.current = true;
      setEnhancementProgress(prev => ({ ...prev, silentRefresh: 'complete' }));
      
      return refreshResult;
      
    } catch (error) {
      console.error('❌ StaffingData: Silent refresh failed:', error);
      setEnhancementProgress(prev => ({ ...prev, silentRefresh: 'failed' }));
      silentRefreshCompleted.current = true;
      return { updated: false, error: error.message };
    } finally {
      setSilentRefreshInProgress(false);
    }
  }, [itRoster, outcomeId, silentRefreshInProgress]);

  // Manual refresh function
  const refreshAssignedMemberData = useCallback(async (allRoles) => {
    if (!allRoles || allRoles.length === 0) {
      console.log('StaffingData: No roles to refresh');
      return;
    }

    setRefreshingRates(true);
    setRateUpdateCount(0);
    setMetadataUpdateCount(0);
    
    const projectYear = getProjectYear(outcomeData, initialFormData, new Date().getFullYear());
    console.log('🔄 StaffingData: Manual refresh for', allRoles.length, 'roles');

    try {
      silentRefreshCompleted.current = false; // allow re-run for manual refresh
      const refreshResult = await performSilentRefresh(allRoles, projectYear);
      
      // Extract update counts
      const totalRateUpdates = refreshResult.rateUpdatesCount || 0;
      const totalMetadataUpdates = refreshResult.metadataUpdatesCount || 0;

      setRateUpdateCount(totalRateUpdates);
      setMetadataUpdateCount(totalMetadataUpdates);

      console.log(`✅ StaffingData: Manual refresh updated ${totalRateUpdates} rates and ${totalMetadataUpdates} metadata records`);

      // Clear counts after 3 seconds
      setTimeout(() => {
        setRateUpdateCount(0);
        setMetadataUpdateCount(0);
      }, 3000);

      return refreshResult;
    } finally {
      setRefreshingRates(false);
    }
  }, [performSilentRefresh, outcomeData, initialFormData]);

  // Clear cache and refresh
  const clearCacheAndRefresh = useCallback(async () => {
    console.log('🔄 StaffingData: Clearing cache and refreshing...');
    silentRefreshCompleted.current = false;
    setRoleUpdateIndicators(new Map());
    setRoleCorruptionIndicators(new Map());
    await loadBasicData();
  }, [loadBasicData]);

  // Handle removing indicators
  const handleRemoveUpdateIndicator = useCallback((roleId) => {
    setRoleUpdateIndicators(prev => {
      const newMap = new Map(prev);
      newMap.delete(roleId);
      return newMap;
    });
  }, []);

  const handleRemoveCorruptionIndicator = useCallback((roleId) => {
    setRoleCorruptionIndicators(prev => {
      const newMap = new Map(prev);
      newMap.delete(roleId);
      return newMap;
    });
  }, []);

  // Load data on mount
  useEffect(() => {
    loadBasicData();
  }, [loadBasicData]);

  // Listen for cache invalidation events
  useEffect(() => {
    const handleCacheInvalidation = (event) => {
      const outcomeIds = event.detail?.outcomeIds;
      if (!outcomeIds || outcomeIds.includes(outcomeId)) {
        console.log('🔄 StaffingData: Cache invalidated, refreshing data');
        clearCacheAndRefresh();
      }
    };

    const handleOutcomeRatesUpdated = (event) => {
      const { outcomeIds, updateDetails } = event.detail;
      if (outcomeIds.includes(outcomeId)) {
        console.log('StaffingData: Received outcome rates updated event');

        if (updateDetails && Array.isArray(updateDetails) && updateDetails.length > 0) {
          const updateIndicators = new Map();
          updateDetails.forEach(update => {
            if (update.role_id) {
              const updateInfo = {
                type: update.type || 'rate',
                oldValue: update.old_value || update.old_rate,
                newValue: update.new_value || update.new_rate,
                details: update.details || 'Updated from Roster'
              };
              updateIndicators.set(update.role_id, updateInfo);
            }
          });

          if (updateIndicators.size > 0) {
            setRoleUpdateIndicators(updateIndicators);
            setTimeout(() => {
              setRoleUpdateIndicators(new Map());
            }, 10000);
          }
        }
      }
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('outcomeCachesInvalidated', handleCacheInvalidation);
      window.addEventListener('outcomeRatesUpdated', handleOutcomeRatesUpdated);
      
      return () => {
        window.removeEventListener('outcomeCachesInvalidated', handleCacheInvalidation);
        window.removeEventListener('outcomeRatesUpdated', handleOutcomeRatesUpdated);
      };
    }
  }, [outcomeId, clearCacheAndRefresh]);

  // Trigger silent refresh when data is ready and roles exist
  useEffect(() => {
    if (basicDataReady && initialFormData?.milestones && itRoster.length > 0 && !silentRefreshCompleted.current) {
      const allRoles = initialFormData.milestones.flatMap(milestone => milestone.roles || []);
      if (allRoles.length > 0) {
        silentRefreshCompleted.current = true; // guard set synchronously to block concurrent re-fires
        const projectYear = getProjectYear(outcomeData, initialFormData, new Date().getFullYear());
        setTimeout(() => {
          performSilentRefresh(allRoles, projectYear);
        }, 1000);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [basicDataReady, initialFormData?.milestones, itRoster, outcomeData]);

  return {
    // Data
    itRoster,
    rateData,
    locationOptions,
    roleOptions,
    
    // State
    loading,
    basicDataReady,
    enhancementProgress,
    silentRefreshInProgress,
    silentRefreshCompleted: silentRefreshCompleted.current,
    silentRefreshResult,
    refreshingRates,
    rateUpdateCount,
    metadataUpdateCount,
    error,
    
    // Indicators
    roleUpdateIndicators,
    roleCorruptionIndicators,
    
    // Actions
    refreshAssignedMemberData,
    clearCacheAndRefresh,
    performSilentRefresh,
    handleRemoveUpdateIndicator,
    handleRemoveCorruptionIndicator
  };
};